import { useInfiniteQuery } from '@tanstack/react-query';
import type {
  AgentApiCandidateProfilesIndex,
  AgentApiCandidateProfilesItem,
} from '@/types/serializers';

interface Params {
  keyword: string;
  organizationUserId?: number;
  customTagIds?: number[];
  enabled?: boolean;
  perPage?: number;
}

type CandidateProfilesQueryKey = readonly [
  string,
  string,
  number | null,
  readonly number[],
  number,
];

export default function useSearchCandidateProfiles({
  keyword,
  organizationUserId,
  customTagIds = [],
  enabled = true,
  perPage = 20,
}: Params) {
  const trimmedKeyword = keyword.trim();
  const normalizedTagIds = [...new Set(customTagIds)].sort((a, b) => a - b);

  return useInfiniteQuery<
    AgentApiCandidateProfilesIndex,
    Error,
    CandidateProfilesSearchResult,
    CandidateProfilesQueryKey,
    number
  >({
    queryKey: [
      'candidateProfilesSearch',
      trimmedKeyword,
      organizationUserId ?? null,
      normalizedTagIds,
      perPage,
    ] as const,
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('per_page', perPage.toString());
      queryParams.append('page', pageParam.toString());

      if (trimmedKeyword.length > 0) {
        queryParams.append('search_query[keyword]', trimmedKeyword);
      }

      if (organizationUserId !== undefined && organizationUserId !== null) {
        queryParams.append('search_query[organization_user_ids][]', organizationUserId.toString());
      }

      if (normalizedTagIds.length > 0) {
        normalizedTagIds.forEach((tagId) =>
          queryParams.append('search_query[custom_tag_ids][]', tagId.toString())
        );
      }

      const queryString = queryParams.toString();
      const response = await fetch(
        queryString.length > 0
          ? `/agent/api/candidate_profiles.json?${queryString}`
          : '/agent/api/candidate_profiles.json'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch candidate profiles');
      }

      const data: AgentApiCandidateProfilesIndex = await response.json();
      return data;
    },
    enabled,
    staleTime: 1000 * 30,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.next_page === null || lastPage.next_page === undefined) {
        return undefined;
      }
      return lastPage.next_page;
    },
    select: (data) => {
      const pages = data.pages ?? [];
      const lastPage = pages[pages.length - 1] ?? null;

      return {
        candidates: pages.flatMap((page) => page.records),
        totalCount: lastPage?.total_count ?? 0,
      };
    },
  });
}

interface CandidateProfilesSearchResult {
  candidates: AgentApiCandidateProfilesItem[];
  totalCount: number;
}
